import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import diagramApi from '../services/diagramApi';

// Default diagram code
const DEFAULT_DIAGRAM = `flowchart TD
    A[Start] --> B{Is it working?}
    B -->|Yes| C[Great!]
    B -->|No| D[Debug]
    D --> B
`;

// Create context
const DiagramContext = createContext();

// Custom hook to use the diagram context
export const useDiagram = () => {
  return useContext(DiagramContext);
};

// Provider component
export const DiagramProvider = ({ children }) => {
  const [code, setCode] = useState(DEFAULT_DIAGRAM);
  const [savedDiagrams, setSavedDiagrams] = useState([]);
  const [currentDiagramId, setCurrentDiagramId] = useState(null);
  const [error, setError] = useState(null);

  // Load saved diagrams on mount
  useEffect(() => {
    loadSavedDiagrams();
  }, []);

  // Load saved diagrams from localStorage
  const loadSavedDiagrams = async () => {
    try {
      const savedDiagramsJson = localStorage.getItem('savedDiagrams');
      if (savedDiagramsJson) {
        const parsedDiagrams = JSON.parse(savedDiagramsJson);
        setSavedDiagrams(parsedDiagrams);
      }
    } catch (err) {
      console.error('Error loading saved diagrams:', err);
      setError('Failed to load saved diagrams');
    }
  };

  // Save diagram
  const saveDiagram = async (title) => {
    try {
      // Process the code to ensure parentheses are escaped
      const processedCode = diagramApi.processMermaidCode(code);
      
      // Check if we're updating an existing diagram
      if (currentDiagramId) {
        // Find the existing diagram
        const existingDiagramIndex = savedDiagrams.findIndex(d => d.id === currentDiagramId);
        
        if (existingDiagramIndex !== -1) {
          // Update the existing diagram
          const updatedDiagrams = [...savedDiagrams];
          updatedDiagrams[existingDiagramIndex] = {
            ...updatedDiagrams[existingDiagramIndex],
            title: title || updatedDiagrams[existingDiagramIndex].title,
            code: processedCode,
            updatedAt: new Date().toISOString()
          };
          
          setSavedDiagrams(updatedDiagrams);
          localStorage.setItem('savedDiagrams', JSON.stringify(updatedDiagrams));
          return currentDiagramId;
        }
      }
      
      // Create a new diagram
      const newDiagram = {
        id: uuidv4(),
        title: title || 'Untitled Diagram',
        code: processedCode,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const updatedDiagrams = [...savedDiagrams, newDiagram];
      setSavedDiagrams(updatedDiagrams);
      setCurrentDiagramId(newDiagram.id);
      
      // Save to localStorage
      localStorage.setItem('savedDiagrams', JSON.stringify(updatedDiagrams));
      
      return newDiagram.id;
    } catch (err) {
      console.error('Error saving diagram:', err);
      setError('Failed to save diagram');
      return null;
    }
  };

  // Load diagram
  const loadDiagram = async (id) => {
    try {
      const diagram = savedDiagrams.find(d => d.id === id);
      if (diagram) {
        setCode(diagram.code);
        setCurrentDiagramId(diagram.id);
        setError(null);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error loading diagram:', err);
      setError('Failed to load diagram');
      return false;
    }
  };

  // Create new diagram
  const newDiagram = () => {
    setCode(DEFAULT_DIAGRAM);
    setCurrentDiagramId(null);
    setError(null);
  };

  // Update code
  const updateCode = (newCode) => {
    // Process the code to ensure parentheses are escaped
    const processedCode = diagramApi.processMermaidCode(newCode);
    setCode(processedCode);
    setError(null);
  };

  // Context value
  const value = {
    code,
    updateCode,
    saveDiagram,
    loadDiagram,
    newDiagram,
    savedDiagrams,
    currentDiagramId,
    error
  };

  return (
    <DiagramContext.Provider value={value}>
      {children}
    </DiagramContext.Provider>
  );
};

export default DiagramContext; 